import { INotePhoto, spLogTags } from "@eventr/common";
import { createLocalCacheStore } from "../editItinerary/expenses/local-expense-store/local-cache-store";
import { createLocalStore } from "../editItinerary/expenses/local-expense-store/local-forage-store";
import { INoteChange } from "./note-state";

/** A queued note change, tagged with the organisation and itinerary it belongs to. */
export interface IQueuedNote {
  org: string;
  itineraryId: number;
  change: INoteChange;
}

/** One entry per note, so autosaves collapse into the latest state rather than piling up. */
const store = createLocalCacheStore<IQueuedNote>("notes", (q) => `note-${q.org}-${q.change.id}`);

/** Photos taken offline, kept until they upload. */
const photos = createLocalStore<Blob>("local-cache-store", "note-photos");

/** What each uploaded photo became, so a change saved before the upload finished still finds it. */
const uploads = createLocalStore<INotePhoto>("local-cache-store", "note-photo-uploads");

const MAX_RETRIES = 5;

/** Takes the organisation as an argument. Importing the bound store here deepens its import cycle. */
export const noteOutbox = {
  queue: async (org: string, itineraryId: number, change: INoteChange) => {
    await store.save({ org, itineraryId, change });
  },

  /** Queued changes for the organisation, oldest first. */
  pending: async (org: string, itineraryId?: number): Promise<IQueuedNote[]> =>
    (await store.getAll())
      .map((item) => item.data)
      .filter((item) => item.org === org && (itineraryId === undefined || item.itineraryId === itineraryId))
      .sort((a, b) => a.change.modifiedAt.localeCompare(b.change.modifiedAt)),

  /** Replaces a queued change only if it has not been saved again since `previous`. */
  replaceIfUnchanged: async (org: string, itineraryId: number, previous: INoteChange, next: INoteChange) => {
    const current = await store.get(`note-${org}-${previous.id}`);
    if (current && current.data.change.modifiedAt === previous.modifiedAt) {
      await store.save({ org, itineraryId, change: next });
    }
  },

  /**
   * Clears the changes a sync carried. A change saved again while the request was in flight
   * has a newer `modifiedAt`, so it stays queued.
   */
  settle: async (org: string, synced: INoteChange[]) => {
    for (const change of synced) {
      const id = `note-${org}-${change.id}`;
      const current = await store.get(id);
      if (current && current.data.change.modifiedAt === change.modifiedAt) {
        await store.remove(id);
      }
    }
  },

  /** Drops a change the server refused for good. */
  discard: async (org: string, noteId: string) => {
    await store.remove(`note-${org}-${noteId}`);
  },

  recordFailure: async (org: string) => {
    for (const item of await store.getAll()) {
      if (item.data.org !== org) continue;

      if (item.retries + 1 >= MAX_RETRIES) {
        spLogTags(`[notes] dropping ${item.storeId} after ${item.retries + 1} attempts`, ["notes"]);
        await store.remove(item.storeId);
        continue;
      }

      await store.incrementRetry(item);
    }
  },

  savePhoto: (localId: string, blob: Blob) => photos.set(localId, blob),
  getPhoto: (localId: string) => photos.get(localId),

  /** Records the upload and frees the device copy. */
  photoUploaded: async (localId: string, uploaded: INotePhoto) => {
    await uploads.set(localId, uploaded);
    await photos.remove(localId);
  },
  getUploaded: (localId: string) => uploads.get(localId),

  clear: async () => {
    for (const item of await store.getAll()) {
      await store.remove(item.storeId);
    }
    await photos.clear();
    await uploads.clear();
  },
};
